import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useNavigate } from 'react-router-dom';
import CustomNavbar from './Navbar/Navbar';
import QuejaModal from './QuejaModal';
import ExportarExcel from './ExportarExcel';
import './ListaQuejas.css';

const ListaQuejas = () => {
    const [quejas, setQuejas] = useState([]);
    const [quejasFiltradas, setQuejasFiltradas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [quejasCompletas, setQuejasCompletas] = useState({});
    const [selectedQueja, setSelectedQueja] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const navigate = useNavigate();

    const [filtros, setFiltros] = useState({
        nombre: '',
        tipoQueja: '',
        fechaInicio: '',
        fechaFin: ''
    });

    const manejarErrorAutenticacion = (error) => {
        if (error.message.includes('401') || error.message.includes('Unauthorized')) {
            console.log('Token expirado o inválido, redirigiendo al login...');
            localStorage.removeItem('token');
            localStorage.removeItem('usuario');
            navigate('/administrador/login');
            return true;
        }
        return false;
    };

    useEffect(() => {
        cargarQuejas();
    }, []);

    useEffect(() => {
        aplicarFiltros();
    }, [filtros, quejas]);


    const cargarQuejas = async () => {
        try {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem('token');

            if (!token) {
                navigate('/administrador/login');
                return;
            }

            const response = await fetch('https://api-sugerencias.conduto.com.ec/quejas', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('401 Unauthorized - Token expirado');
                }
                throw new Error('Error al cargar los datos');
            }

            const result = await response.json();

            if (result.success) {
                const quejasConExpiracion = result.data.map(queja => {
                    if (quejaEstaExpirada(queja)) {
                        return {
                            ...queja,
                            estado: 'expirado'
                        };
                    }
                    return queja;
                });

                setQuejas(quejasConExpiracion);
                setQuejasFiltradas(quejasConExpiracion);
            } else {
                throw new Error(result.message);
            }
        } catch (error) {
            console.error('Error:', error);

            if (manejarErrorAutenticacion(error)) {
                return;
            }

            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const marcarComoAsignada = async (id) => {
        try {
            const token = localStorage.getItem('token');

            if (!token) {
                navigate('/administrador/login');
                return;
            }

            const response = await fetch(`https://api-sugerencias.conduto.com.ec/queja/${id}/marcar-asignada`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Error al marcar como asignada');
            }

            const result = await response.json();

            if (!result.success) {
                throw new Error(result.message);
            }

            const fechaActualizacion = result.data?.fechaActualizacion || new Date().toISOString();

            setQuejas(prev => prev.map(q =>
                q.id === id ? {
                    ...q,
                    estado: 'en_proceso',
                    fecha_actualizacion: fechaActualizacion
                } : q
            ));
            setQuejasFiltradas(prev => prev.map(q =>
                q.id === id ? {
                    ...q,
                    estado: 'en_proceso',
                    fecha_actualizacion: fechaActualizacion
                } : q
            ));

            return result.data;

        } catch (error) {
            console.error('Error al marcar como asignada:', error);
            throw error;
        }
    };

    const cargarQuejaCompleta = async (id) => {
        try {
            setLoadingDetails(true);
            const token = localStorage.getItem('token');

            if (!token) {
                navigate('/administrador/login');
                return;
            }

            const response = await fetch(`https://api-sugerencias.conduto.com.ec/queja/${id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('401 Unauthorized - Token expirado');
                }
                throw new Error('Error al cargar los detalles');
            }

            const result = await response.json();

            if (result.success) {
                setQuejasCompletas(prev => ({
                    ...prev,
                    [id]: result.data
                }));
                return result.data;
            } else {
                throw new Error(result.message);
            }
        } catch (error) {
            console.error('Error al cargar detalles:', error);

            if (manejarErrorAutenticacion(error)) {
                return;
            }

            throw error;
        } finally {
            setLoadingDetails(false);
        }
    };

    const abrirModalQueja = async (queja) => {
        setSelectedQueja(queja);
        setShowModal(true);

        if (!quejasCompletas[queja.id]) {
            try {
                await cargarQuejaCompleta(queja.id);
            } catch (error) {
                console.error('Error al cargar detalles:', error);

                if (!error.message.includes('401')) {
                    setError(`Error al cargar detalles: ${error.message}`);
                }
            }
        }
    };

    const cerrarModal = () => {
        setShowModal(false);
        setSelectedQueja(null);
    };

    const actualizarEstadoQueja = async (id, estado) => {
        try {
            const token = localStorage.getItem('token');

            if (!token) {
                navigate('/administrador/login');
                return;
            }

            const response = await fetch(`https://api-sugerencias.conduto.com.ec/queja/${id}/estado`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ estado })
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('401 Unauthorized - Token expirado');
                }
                throw new Error('Error al actualizar estado');
            }

            const result = await response.json();

            if (!result.success) {
                throw new Error(result.message);
            }

            return result;

        } catch (error) {
            console.error('Error al actualizar estado:', error);

            if (manejarErrorAutenticacion(error)) {
                return;
            }

            throw error;
        }
    };

    const marcarComoResuelta = async (id) => {
        try {
            const result = await actualizarEstadoQueja(id, 'resuelta');

            const fechaActualizacion = result.data?.fechaActualizacion || new Date().toISOString();

            setQuejas(prev => prev.map(q =>
                q.id === id ? {
                    ...q,
                    estado: 'resuelta',
                    fecha_actualizacion: fechaActualizacion
                } : q
            ));
            setQuejasFiltradas(prev => prev.map(q =>
                q.id === id ? {
                    ...q,
                    estado: 'resuelta',
                    fecha_actualizacion: fechaActualizacion
                } : q
            ));
            if (selectedQueja && selectedQueja.id === id) {
                setSelectedQueja(prev => ({
                    ...prev,
                    estado: 'resuelta',
                    fecha_actualizacion: fechaActualizacion
                }));
            }
        } catch (error) {
            if (!error.message.includes('401')) {
                setError(`Error al marcar como resuelta: ${error.message}`);
            }
        }
    };

    const descargarEvidencia = async (filename) => {
        if (!filename) {
            console.error('Nombre de archivo no proporcionado');
            return;
        }

        try {
            const token = localStorage.getItem('token');

            if (!token) {
                alert('Error: No hay sesión activa. Por favor, inicie sesión nuevamente.');
                navigate('/administrador/login');
                return;
            }

            console.log('Iniciando descarga de:', filename);

            const response = await fetch(`https://api-sugerencias.conduto.com.ec/descargar-evidencia/${filename}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('401 Unauthorized - Token expirado');
                }
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }

            const blob = await response.blob();

            if (blob.size === 0) {
                throw new Error('El archivo está vacío o no se pudo descargar');
            }

            const blobUrl = window.URL.createObjectURL(blob);

            const downloadLink = document.createElement('a');
            downloadLink.href = blobUrl;
            downloadLink.download = filename;
            downloadLink.style.display = 'none';

            document.body.appendChild(downloadLink);
            downloadLink.click();

            document.body.removeChild(downloadLink);
            window.URL.revokeObjectURL(blobUrl);

            console.log('Archivo descargado exitosamente:', filename);

        } catch (error) {
            console.error('Error al descargar evidencia:', error);

            if (manejarErrorAutenticacion(error)) {
                return;
            }

            alert(`Error al descargar el archivo: ${error.message}`);
        }
    };

    const formatearFecha = (fecha) => {
        if (!fecha) return '----';
        const d = new Date(fecha);
        const f = d.toLocaleDateString('es-ES');
        const h = d.toLocaleTimeString('es-ES', { hour12: false });
        return `${f} - ${h}`;
    };

    const formatearFechaCorta = (fecha) => {
        if (!fecha) return '';
        const d = new Date(fecha);
        const fechaCorta = d.toLocaleDateString('es-ES');
        const hora = d.toLocaleTimeString('es-ES', { hour12: false, hour: '2-digit', minute: '2-digit' });
        return `${fechaCorta} - ${hora}`;
    };

    const formatearFechaAsignacionOTerminacion = (queja) => {
        if (queja.estado === 'pendiente') {
            return '';
        }

        if (queja.fecha_actualizacion && queja.fecha_actualizacion !== queja.fecha_creacion) {
            return formatearFechaCorta(queja.fecha_actualizacion);
        }

        return '';
    };

    const obtenerColorEstado = (estado) => {
        switch (estado) {
            case 'pendiente':
                return '#FFC107';
            case 'en_proceso':
                return '#17A2B8';
            case 'resuelta':
                return '#28A745';
            case 'expirado':
                return '#DC3545';
            default:
                return '#6C757D';
        }
    };

    const obtenerTextoEstado = (estado) => {
        switch (estado) {
            case 'pendiente':
                return 'Pendiente';
            case 'en_proceso':
                return 'En Proceso';
            case 'resuelta':
                return 'Resuelta';
            case 'expirado':
                return 'Expirado';
            default:
                return estado;
        }
    };

    const quejaEstaExpirada = (queja) => {
        if (queja.estado === 'expirado') {
            return true;
        }

        if (queja.fecha_limite) {
            const fechaLimite = new Date(queja.fecha_limite);
            const ahora = new Date();

            if (fechaLimite < ahora && queja.estado !== 'resuelta') {
                return true;
            }
        }

        return false;
    };

    const actualizarEstadoExpirado = async (id) => {
        try {
            const token = localStorage.getItem('token');

            if (!token) {
                navigate('/administrador/login');
                return;
            }

            const response = await fetch(`https://api-sugerencias.conduto.com.ec/queja/${id}/estado`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ estado: 'expirado' })
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('401 Unauthorized - Token expirado');
                }
                throw new Error('Error al actualizar estado a expirado');
            }

            const result = await response.json();

            if (!result.success) {
                throw new Error(result.message);
            }

            return result;

        } catch (error) {
            console.error('Error al actualizar estado a expirado:', error);

            if (manejarErrorAutenticacion(error)) {
                return;
            }

            throw error;
        }
    };

    const obtenerIconoTipo = (tipo) => {
        return tipo === 'anonima';
    };

    const obtenerTextoTipo = (tipo) => {
        return tipo === 'anonima' ? 'Anónima' : 'Identificada';
    };

    const obtenerIniciales = (nombre) => {
        if (!nombre || nombre === 'Anónimo') return 'A';
        return nombre.charAt(0).toUpperCase();
    };

    const handleFiltroChange = (e) => {
        const { name, value } = e.target;
        setFiltros(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const limpiarFiltros = () => {
        setFiltros({
            nombre: '',
            tipoQueja: '',
            fechaInicio: '',
            fechaFin: ''
        });
    };

    const aplicarFiltros = () => {
        let filtradas = quejas;

        if (filtros.nombre) {
            filtradas = filtradas.filter(queja => {
                const nombreCompleto = queja.nombre?.toLowerCase() || 'anónimo';
                return nombreCompleto.includes(filtros.nombre.toLowerCase());
            });
        }

        if (filtros.tipoQueja) {
            filtradas = filtradas.filter(queja =>
                queja.tipo_queja === filtros.tipoQueja
            );
        }

        if (filtros.fechaInicio || filtros.fechaFin) {
            filtradas = filtradas.filter(queja => {
                const fechaQueja = new Date(queja.fecha_creacion);
                let cumpleInicio = true;
                let cumpleFin = true;

                if (filtros.fechaInicio) {
                    const fechaInicio = new Date(filtros.fechaInicio);
                    fechaInicio.setHours(0, 0, 0, 0);
                    cumpleInicio = fechaQueja >= fechaInicio;
                }

                if (filtros.fechaFin) {
                    const fechaFin = new Date(filtros.fechaFin);
                    fechaFin.setHours(23, 59, 59, 999);
                    cumpleFin = fechaQueja <= fechaFin;
                }

                return cumpleInicio && cumpleFin;
            });
        }

        setQuejasFiltradas(filtradas);
    };

    if (loading) {
        return (
            <div className="container mt-4">
                <div className="d-flex justify-content-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                </div>
                <p className="text-center mt-2">Cargando sugerencias...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mt-4">
                <div className="alert alert-danger" role="alert">
                    <h4 className="alert-heading">Error</h4>
                    <p>{error}</p>
                    <button className="btn btn-primary" onClick={cargarQuejas}>
                        Reintentar
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <CustomNavbar />
            <div className="container-quejas" style={{ minHeight: '100vh', padding: '20px' }}>
                <div className="d-flex justify-content-between align-items-center m-4">
                    <h1 style={{ color: '#004892', fontWeight: 'arial', fontSize: '28px' }}>Lista de Sugerencias</h1>
                    <div className="d-flex align-items-center">
                        <div className="d-flex align-items-center">
                            <span style={{ color: '#666', marginRight: '10px' }}>Ver {quejasFiltradas.length} | de {quejas.length}</span>
                            <ExportarExcel quejas={quejas} filtradas={quejasFiltradas} />
                        </div>
                    </div>
                </div>

                <div className="card mb-4 border-0 shadow-sm">
                    <div className="card-body border-bottom">
                        <div className="row g-3">
                            <div className="col-md-3">
                                <label className="form-label fw-semibold">Nombre</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Buscar por..."
                                    name="nombre"
                                    value={filtros.nombre}
                                    onChange={handleFiltroChange}
                                />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label fw-semibold">Tipo de Sugerencia</label>
                                <select
                                    className="form-select"
                                    id="tipoQueja"
                                    name="tipoQueja"
                                    value={filtros.tipoQueja}
                                    onChange={handleFiltroChange}
                                >
                                    <option value="">Todos los tipos</option>
                                    <option value="anonima">Anónima</option>
                                    <option value="identificada">Identificada</option>
                                </select>
                            </div>
                            <div className="col-md-2">
                                <label className="form-label fw-semibold">Fecha desde</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    name="fechaInicio"
                                    value={filtros.fechaInicio}
                                    onChange={handleFiltroChange}
                                />
                            </div>
                            <div className="col-md-2">
                                <label className="form-label fw-semibold">Fecha hasta</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    name="fechaFin"
                                    value={filtros.fechaFin}
                                    onChange={handleFiltroChange}
                                />
                            </div>
                            <div className="col-md-2 d-flex align-items-end">
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={limpiarFiltros}
                                >
                                    Limpiar Filtros
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card border-0 shadow-sm">
                    <div className="card-body p-0">
                        {quejasFiltradas.length === 0 ? (
                            <div className="text-center py-5">
                                <i className="bi bi-chat-square-text display-1 text-muted"></i>
                                <h4 className="mt-3 text-muted ">No hay sugerencias que coincidan con los filtros</h4>
                                <button
                                    className="btn btn-primary mt-2"
                                    onClick={limpiarFiltros}
                                >
                                    Limpiar filtros
                                </button>
                            </div>
                        ) : (
                            <div
                                className="table-responsive"
                                style={{
                                    maxHeight: "65vh",
                                    overflowY: "auto"
                                }}
                            >
                                <table className="table table-hover mb-0 border">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: '10px' }}>ID</th>
                                            <th style={{ width: '80px' }}></th>
                                            <th style={{ width: '690px' }}>Nombre</th>
                                            <th style={{ width: '170px' }}>Fecha de Envío</th>
                                            <th style={{ width: '180px' }}>Fecha de Asignado/Terminado</th>
                                            <th style={{ width: '120px' }}>Estado</th>
                                            <th style={{ width: '100px' }}>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {quejasFiltradas.map((queja) => (
                                            <tr key={queja.id} className="hover-effect">
                                                <td className="align-middle">{queja.id}</td>
                                                <td>
                                                    <div className="d-flex justify-content-center">
                                                        <div
                                                            className={`rounded-circle d-flex align-items-center justify-content-center ${queja.tipo_queja === 'anonima'
                                                                ? 'bg-secondary text-white'
                                                                : 'bg-primary text-white'
                                                                }`}
                                                            style={{
                                                                width: '50px',
                                                                height: '50px',
                                                                fontSize: '20px'
                                                            }}
                                                            title={obtenerTextoTipo(queja.tipo_queja)}
                                                        >
                                                            {queja.tipo_queja === 'anonima' ? (
                                                                'A'
                                                            ) : (
                                                                queja.nombre ? obtenerIniciales(queja.nombre) : <i className="bi bi-person-fill"></i>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div>
                                                        <h6 className="mb-1 fw-bold" style={{ color: '#333' }}>
                                                            {queja.nombre || 'Anónimo'}
                                                        </h6>
                                                        <small className="text-muted">
                                                            {obtenerTextoTipo(queja.tipo_queja)}
                                                        </small>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="text-muted">
                                                        {formatearFechaCorta(queja.fecha_creacion)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="text-muted">
                                                        {formatearFechaAsignacionOTerminacion(queja)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span
                                                        className="badge fw-normal"
                                                        style={{
                                                            backgroundColor: obtenerColorEstado(queja.estado),
                                                            color: queja.estado === 'pendiente' ? '#000' : '#fff',
                                                            fontSize: '0.8em'
                                                        }}
                                                    >
                                                        {obtenerTextoEstado(queja.estado)}
                                                        {quejaEstaExpirada(queja) && queja.estado !== 'expirado' && (
                                                            <span className="ms-1" title="Fecha límite expirada">
                                                            </span>
                                                        )}
                                                    </span>
                                                </td>
                                                <td>
                                                    <button
                                                        className="btn btn-outline-primary btn-sm"
                                                        onClick={() => abrirModalQueja(queja)}
                                                        title="Ver detalles de la sugerencia"
                                                    >
                                                        Ver
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                <QuejaModal
                    show={showModal}
                    onHide={cerrarModal}
                    selectedQueja={selectedQueja}
                    quejasCompletas={quejasCompletas}
                    setQuejasCompletas={setQuejasCompletas}
                    loadingDetails={loadingDetails}
                    obtenerIconoTipo={obtenerIconoTipo}
                    obtenerTextoTipo={obtenerTextoTipo}
                    obtenerColorEstado={obtenerColorEstado}
                    obtenerTextoEstado={obtenerTextoEstado}
                    formatearFecha={formatearFecha}
                    descargarEvidencia={descargarEvidencia}
                    marcarComoResuelta={marcarComoResuelta}
                    setQuejas={setQuejas}
                    setQuejasFiltradas={setQuejasFiltradas}
                    setSelectedQueja={setSelectedQueja}
                    marcarComoAsignada={marcarComoAsignada}
                />
            </div>
        </>
    );
};

export default ListaQuejas;